'use strict';

import $ from 'jquery';

/**
 * Handle lazy load stuff on the page.
 * - Images in car lifts
 * - Images in carousels
 */
const lazy = {
  _lastHandled: 0, // When was the last time lazy load objects were handled
  _maxHandleInterval: 1000, // handleAllLazies can only be called once every {maxHandleInterval} ms
  _triggerDistance: 500, // Max distance of lazy element from viewport that triggers handleLazy, px

  /**
   * Handle lazy load of a single element:
   * if element is visible, show lazy loadable content
   *
   * @param {jQuery} $element The lazy element
   */
  handleLazy: function($element) {
    if (this._elementIsVisible($element)) {
      const lazyContent = $element.data('lazy');
      const lazyType = $element.data('lazy-type') || 'src';

      switch (lazyType) {
      case 'src':
        $element.attr('src', lazyContent);
        break;
      case 'bg':
        $element.css({ backgroundImage: 'url(' + lazyContent + ')' });
        break;
      }

      // Remove data-lazy attributes so this element won't be triggered again
      // But don't do this to slick slider items or it will mess up the carousel on viewport width change
      if (!$element.hasClass('image-carousel-nav-item')) {
        $element.removeData('lazy').removeData('lazy-type').removeAttr('data-lazy').removeAttr('data-lazy-type');
      }
    }
  },

  /**
   * Find out if an element is visible on the screen
   * Element is not visible if it is out of the viewport,
   * or in a carousel and not visible
   *
   * @param {jQuery} $element The element to test
   * @return {boolean}        True if element is visible, false if not
   */
  _elementIsVisible: function($element) {
    if ($element.length === 0) {
      return false;
    }

    const elementOffset = $element.offset();
    elementOffset.bottom = elementOffset.top + $element.outerHeight();
    elementOffset.right = elementOffset.left + $element.outerWidth();

    const $window = $(window);

    const viewportOffset = {
      top: $window.scrollTop(),
      left: $window.scrollLeft()
    }
    viewportOffset.bottom = viewportOffset.top + $window.outerHeight();
    viewportOffset.right = viewportOffset.left + $window.outerWidth();

    // Take this._triggerDistance into account
    viewportOffset.bottom += this._triggerDistance;
    viewportOffset.top -= this._triggerDistance;
    viewportOffset.left -= this._triggerDistance;
    viewportOffset.right += this._triggerDistance;

    // Check if element is inside viewport
    if (
      elementOffset.bottom < viewportOffset.top ||
      elementOffset.top > viewportOffset.bottom ||
      elementOffset.left > viewportOffset.right ||
      elementOffset.right < viewportOffset.left
    ) {
      return false;
    }

    return true;
  },

  /**
   * Loop through each lazy loadable element and handle it
   * This can be done max once per every {this._maxHandleInterval}
   * If this function is called too early, it will delay the execution
   */
  handleAllLazies: function() {
    // Make sure this function is executed max once per every {this._maxHandleInterval}
    const timeUntilHandlingAllowed = Date.now() - (this._lastHandled + this._maxHandleInterval);
    if (timeUntilHandlingAllowed < 0) {
      // Too little time since last handling.
      // Handle later.
      clearTimeout(this._handleTimeout);
      this._handleTimeout = setTimeout(this.handleAllLazies.bind(this), timeUntilHandlingAllowed);
      return;
    }

    // Do actual handling
    const self = this;
    $('[data-lazy]').each(function(){
      self.handleLazy($(this));
    });

    this._lastHandled = Date.now();
  },

  init: function() {
    this.handleAllLazies();
    $(window).scroll(this.handleAllLazies.bind(this));
    $('body').on('afterChange', this.handleAllLazies.bind(this));
  }
};

module.exports = lazy;
